import { useClientPagination } from '@/hooks/useClientPagination';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  CarryFundPortfolio,
  CarryMemberPortfolio,
  CarryPortfolioCompany,
} from '@/types/carryFund';
import { CarryStatStrip } from './CarryStatStrip';
import {
  MUTED_NUM_CELL,
  NUM_CELL,
  formatMultiple,
  formatShare,
  formatThousands,
} from './carryFundValues';
import { portfolioStats } from './carryPortfolioStats';

function Amount({ value, muted = false }: { value: string; muted?: boolean }) {
  return (
    <TableCell className={muted ? MUTED_NUM_CELL : NUM_CELL}>
      {Number(value) ? formatThousands(value) : '–'}
    </TableCell>
  );
}

export function CarryPortfolioTab({
  portfolio,
}: {
  portfolio: CarryFundPortfolio | CarryMemberPortfolio;
}) {
  return (
    <div className="space-y-6">
      <CarryStatStrip stats={portfolioStats(portfolio.summary)} />
      <CarryPortfolioCompanies companies={portfolio.companies} />
    </div>
  );
}

export function CarryPortfolioCompanies({
  companies,
}: {
  companies: CarryPortfolioCompany[];
}) {
  const { pageItems: shown, paginationProps } = useClientPagination(
    companies,
    10
  );

  return (
    <div className="space-y-6">
      {companies.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          This fund has no portfolio companies yet.
        </p>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead className={NUM_CELL}>Invested</TableHead>
                  <TableHead className={NUM_CELL}>Initial</TableHead>
                  <TableHead className={NUM_CELL}>Follow-on</TableHead>
                  <TableHead className={NUM_CELL}>Equity</TableHead>
                  <TableHead className={NUM_CELL}>CLA/SAFE</TableHead>
                  <TableHead className={NUM_CELL}>Distributed</TableHead>
                  <TableHead className={NUM_CELL}>NAV</TableHead>
                  <TableHead className={NUM_CELL}>MOM</TableHead>
                  <TableHead className={NUM_CELL}>Stake</TableHead>
                  <TableHead className={NUM_CELL}>Months</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((company) => (
                  <TableRow key={company.company_id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {company.name}
                        {company.exited && (
                          <Badge variant="secondary">Exited</Badge>
                        )}
                      </span>
                    </TableCell>
                    <Amount value={company.invested} />
                    <Amount value={company.initial} muted />
                    <Amount value={company.follow_on} muted />
                    <Amount value={company.equity} muted />
                    <Amount value={company.convertible} muted />
                    <Amount value={company.distributed} />
                    <TableCell className={NUM_CELL}>
                      {company.nav === null
                        ? '–'
                        : formatThousands(company.nav)}
                    </TableCell>
                    <TableCell className={NUM_CELL}>
                      {company.mom === null ? '–' : formatMultiple(company.mom)}
                    </TableCell>
                    <TableCell className={NUM_CELL}>
                      {company.stake === null
                        ? '–'
                        : formatShare(Number(company.stake), 1)}
                    </TableCell>
                    <TableCell className={MUTED_NUM_CELL}>
                      {company.months}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            {...paginationProps}
            showSizeChanger
            pageSizeOptions={[10, 25, 50]}
          />
        </>
      )}
    </div>
  );
}
